import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import RegistrationPage from './pages/RegistrationPage';
import CompanyAdminDashboard from './pages/CompanyAdminDashboard';
import MasterAdminDashboard from './pages/MasterAdminDashboard';
import UserManagementPage from './pages/UserManagementPage';
import UserDashboard from './pages/UserDashboard';
import RequirementResultsPage from './pages/RequirementResultsPage';
import ScheduleMeetingPage from './pages/ScheduleMeetingPage';
import MeetingRoomPage from './pages/MeetingRoomPage';
import MyMeetingsPage from './pages/MyMeetingsPage';
import SettingsPage from './pages/SettingsPage';
import EditScreeningQuestions from './pages/EditScreeningQuestions';
import Phase3InterviewDetail from './components/Phase3InterviewDetail';
import CandidateInterviewPage from './pages/CandidateInterviewPage';
import MyInterviews from './pages/MyInterviews';
import InterviewResponsePage from './pages/InterviewResponsePage';
import InterviewRoundResponsePage from './pages/InterviewRoundResponsePage';
import TermsPage from './pages/TermsPage';
import { LEGACY_MEETINGS } from './config/features';
import RetiredMeetingsPage from './components/RetiredMeetingsPage';
import PrivacyPage from './pages/PrivacyPage';
import AcceptableUsePage from './pages/AcceptableUsePage';
import SecurityPage from './pages/SecurityPage';
import GDPRPage from './pages/GDPRPage';
import IntegrationsPage from './pages/IntegrationsPage';
import './App.css';
import SupportDashboard from './components/SupportDashboard';
import ResetPasswordPage from './pages/ResetPasswordPage';
import JobIntakeFormPage from './pages/JobIntakeFormPage';
import JobIntakeCallPage from './pages/JobIntakeCallPage';
import JobReviewPage from './pages/JobReviewPage';
import OrganizationPage from './pages/OrganizationPage';
import JobsPage from './pages/JobsPage';
import JobPage from './pages/JobPage';
import ApprovalsPage from './pages/ApprovalsPage';
import CandidatesPage from './pages/CandidatesPage';
import CandidateProfilePage from './pages/CandidateProfilePage';
import TechnicalScreenPage from './pages/TechnicalScreenPage';
import { InterviewPacketPage, MyInterviewsPage, ReviewPage } from './pages/InterviewPages';
import JobCandidatesPage from './pages/JobCandidatesPage';
import NotificationsPage from './pages/NotificationsPage';
import ActivatePage from './pages/ActivatePage';

// ── Role-protected route wrapper ────────────────────────────────────────────
// Renders children only if the logged-in user's role is in allowedRoles.
// Redirects to '/' (login) if no token, or to '/dashboard/user' if wrong role.
function ProtectedRoute({ allowedRoles, children }) {
  const token = localStorage.getItem('token');

  if (!token) return <Navigate to="/" replace />;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const role    = (payload.role || '').toLowerCase();
    const allowed = allowedRoles.map(r => r.toLowerCase());
    if (!allowed.includes(role)) return <Navigate to="/" replace />;
  } catch (e) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Company admins manage people in Organization; Manage Users stays for master admins.
function ManageUsersRoute() {
  let role;
  try { role = (JSON.parse(localStorage.getItem('user') || '{}') || {}).role; } catch (e) { role = undefined; }
  return role === 'company_super_admin' ? <Navigate to="/organization" replace /> : <UserManagementPage />;
}

// The old job page URL (notifications, e-mails, bookmarks) → the job page. Master admins (no company) keep it.
function ViewRequirementRoute() {
  const { requirementId } = useParams();
  let role;
  try { role = (JSON.parse(localStorage.getItem('user') || '{}') || {}).role; } catch (e) { role = undefined; }
  return role === 'master_admin' ? <RequirementResultsPage /> : <Navigate to={`/jobs/${requirementId}`} replace />;
}

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/register" element={<RegistrationPage />} />
          <Route path="/dashboard/company-admin" element={<CompanyAdminDashboard />} />
          <Route path="/dashboard/master-admin" element={<MasterAdminDashboard />} />
          <Route path="/manage-users" element={<ManageUsersRoute />} />
          <Route path="/organization" element={<OrganizationPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/activate" element={<ActivatePage />} />
          <Route path="/dashboard/user" element={<UserDashboard />} />
          <Route path="/requirement-results" element={<RequirementResultsPage />} />
          <Route path="/view-requirement/:requirementId" element={<ViewRequirementRoute />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/approvals" element={<ApprovalsPage />} />
          <Route path="/candidates" element={<CandidatesPage />} />
          <Route path="/candidates/:id" element={<CandidateProfilePage />} />
          <Route path="/technical-screen" element={<TechnicalScreenPage />} />
          <Route path="/my-interview-rounds" element={<MyInterviewsPage />} />
          <Route path="/interview-rounds/:id" element={<InterviewPacketPage />} />
          <Route path="/applications/:id/review" element={<ReviewPage />} />
          <Route path="/jobs/:id/candidates" element={<JobCandidatesPage />} />
          <Route path="/jobs/:id" element={<JobPage />} />
          <Route path="/jobs/:requirementId/classic" element={<RequirementResultsPage />} />
          <Route path="/schedule-meeting" element={LEGACY_MEETINGS ? <ScheduleMeetingPage /> : <RetiredMeetingsPage />} />
          <Route path="/meeting/:meetingId" element={LEGACY_MEETINGS ? <MeetingRoomPage /> : <RetiredMeetingsPage />} />
          <Route path="/my-meetings" element={LEGACY_MEETINGS ? <MyMeetingsPage /> : <RetiredMeetingsPage />} />
          <Route path="/edit-screening/:requirementId" element={<EditScreeningQuestions />} />
          <Route path="/settings" element={<SettingsPage />} />
          {/* Phase 2 — HM-led job creation (HM_JOB_CREATION_COMPANIES) */}
          <Route path="/jobs/intake/:intakeId" element={<JobIntakeFormPage />} />
          <Route path="/jobs/intake/:intakeId/call" element={<JobIntakeCallPage />} />
          <Route path="/jobs/intake/:intakeId/review" element={<JobReviewPage />} />
          {/* Phase 3 Routes */}
          <Route path="/phase3/interview/:id" element={<Phase3InterviewDetail />} />
          <Route path="/interview/:interviewId" element={<CandidateInterviewPage />} />
          <Route path="/interview/:interviewId/join" element={<CandidateInterviewPage />} />
          <Route path="/interview/:interviewId/respond" element={<InterviewResponsePage />} />
          <Route path="/interview-response" element={<InterviewRoundResponsePage />} />
          <Route path="/my-interviews" element={<MyInterviews />} />
          {/* Legal & Info Pages */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/acceptable-use" element={<AcceptableUsePage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/gdpr" element={<GDPRPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/support" element={
            <ProtectedRoute allowedRoles={['support', 'master_admin']}>
              <SupportDashboard />
            </ProtectedRoute>
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;